import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Heart, ChatCircle, UserPlus, Check } from '@phosphor-icons/react';

const iconMap = {
  like: <Heart size={20} weight="duotone" className="text-red-500" />,
  comment: <ChatCircle size={20} weight="duotone" className="text-blue-500" />,
  follow: <UserPlus size={20} weight="duotone" className="text-primary" />
};

export const NotificationsDropdown = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications`,
        { withCredentials: true }
      );
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch (error) {
      // Silent fail
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/read-all`,
        {},
        { withCredentials: true }
      );
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {}
  };

  const handleClickNotification = async (notif) => {
    if (!notif.read) {
      try {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notif.notification_id}/read`,
          {},
          { withCredentials: true }
        );
        setUnreadCount(Math.max(0, unreadCount - 1));
      } catch (error) {}
    }

    if (notif.type === 'follow' && notif.related_id) {
      navigate(`/profile/${notif.related_id}`);
    } else if ((notif.type === 'like' || notif.type === 'comment') && notif.related_id) {
      navigate(`/article/${notif.related_id}`);
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="notifications-button"
        className="relative p-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        <Bell size={24} weight="duotone" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1" data-testid="unread-count">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-50" data-testid="notifications-dropdown">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h4 className="font-semibold">Notifications</h4>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                data-testid="mark-all-read"
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <Check size={14} />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center" data-testid="no-notifications">
                <Bell size={32} weight="duotone" className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <button
                  key={notif.notification_id}
                  onClick={() => handleClickNotification(notif)}
                  data-testid={`notification-${notif.notification_id}`}
                  className={`w-full text-left p-4 border-b border-border hover:bg-muted transition-colors flex items-start gap-3 ${!notif.read ? 'bg-primary/5' : ''}`}
                >
                  <div className="mt-0.5">{iconMap[notif.type] || <Bell size={20} />}</div>
                  <div className="flex-1">
                    <p className="text-sm">{notif.content}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(notif.created_at).toLocaleString()}
                    </p>
                  </div>
                  {!notif.read && <div className="w-2 h-2 rounded-full bg-primary mt-2"></div>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsDropdown;
